import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { paywallFlowStore } from '@/stores/PaywallFlowStore';
import { isApplePayCancelledError, useTrialPayment } from '@/hooks/useTrialPayment';
import { usePolicyText } from '@/hooks/usePolicyText';
import { sendLoadSuccess, sendPaymentFailed } from '@/hooks/useWebViewMessaging';
import { PaymentError } from '@/components/ui/PaymentError';
import { RestoreSubscription } from '@/components/paywall/RestoreSubscription';
import { syncPaywallMetaFromUrl } from '@/utils/paywallUrlParams';

const VIDEO_SRC = '/adult-video.mp4';
const PAGE_BG = '#1c1c1c';
/** Временно отключить видео на фоне (включить: true) */
const SHOW_VIDEO = true;

/** Внешний фон на весь экран — тот же тёмный цвет, что и в колонке */
const PageOuter = styled.div`
  width: 100%;
  min-height: 100vh;
  min-height: 100svh;
  background: ${PAGE_BG};
`;

/** Колонка с ограничением ширины (как в v6) */
const PageColumn = styled.div`
  position: relative;
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  min-height: 100svh;
  padding-top: var(--app-safe-area-top);
  padding-bottom: var(--app-safe-area-bottom);
  background: ${PAGE_BG};
`;

const Screen = styled.div`
  position: relative;
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${PAGE_BG};
  z-index: 1;
  min-height: 100svh;
  min-height: var(--app-safe-area-viewport-height);
`;

const PageBg = styled.div`
  position: absolute;
  inset: 0;
  background: ${PAGE_BG};
  z-index: 0;
`;

/* Видео — верхняя половина экрана; маска создаёт плавное исчезновение внизу; ограничено шириной колонки */
const VideoBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50vh;
  height: 50dvh;
  z-index: 2;
  overflow: hidden;
  background: ${PAGE_BG};
  mask-image: linear-gradient(
    to bottom,
    black 0%,
    black 55%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    black 0%,
    black 55%,
    transparent 100%
  );
  mask-size: 100% 100%;
  -webkit-mask-size: 100% 100%;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* Лёгкое затемнение сверху; низ — прозрачный, виден фон #1c1c1c; ограничено шириной колонки */
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.25) 0%,
    rgba(0, 0, 0, 0.3) 30%,
    rgba(28, 28, 28, 0.2) 55%,
    transparent 70%,
    transparent 100%
  );
  z-index: 3;
  pointer-events: none;
`;

const VideoSection = styled.div`
  width: 100%;
  height: 30vh;
`;

/** Прижимает основной контент к низу экрана (как в v6) */
const ContentPush = styled.div`
  margin-top: auto;
`;

/** Обёртка контента из v4 с отступами */
const ContentInner = styled.main`
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;
`;

/** Обёртка страницы: экран + футер (как в v6) */
const PageLayout = styled.div`
  z-index: 1;
`;

/** Обёртка футера: Legal + Restore (как в v6) */
const FooterWrap = styled.div`
  position: relative;
  z-index: 1;
  color: white;
  padding: 0 16px 16px;
`;

const FooterSection = styled.div`
  padding-top: 8px;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 700;
  font-size: 30px;
  line-height: 1.3;
  text-align: center;
  color: #fff;
`;

const Offer = styled.p`
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  color: #fff;
`;

const OrangeAccent = styled.span`
  color: #fd9819;
  font-weight: 600;
`;

const Price = styled.p`
  margin: 0 0 12px;
  font-size: 16px;
  text-align: center;
  color: #fff;
  opacity: 0.5;
`;

const CtaWrap = styled.div`
  width: 100%;
  margin-bottom: 12px;
  box-sizing: border-box;
`;

const CtaButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 16px 24px;
  border-radius: 9999px;
  background: #fd9819;
  font-size: 18px;
  font-weight: 500;
  text-align: center;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  -webkit-tap-highlight-color: transparent;
  min-height: 56px;
  transition: opacity 0.2s;

  &:active:not(:disabled) {
    opacity: 0.9;
  }
`;

const NoCharge = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  text-align: center;
  color: #fff;
  opacity: 0.9;
`;

const CheckIcon = styled.span`
  color: #fd9819;
  margin-right: 4px;
`;

const Legal = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: rgb(80, 80, 80);
  text-align: justify;
  
  a {
    color: rgb(120, 120, 120);
    text-decoration: underline;
  }
`;

const ErrorWrapper = styled.div`
  margin: 0 0 12px 0;
`;

function PaywallV4Inner() {
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(true);
  const applePayLoadStatusReportedRef = useRef(false);

  const {
    initializeApplePay,
    showApplePay,
    preferredPaymentMethod,
    isInitializing,
    isApplePayReady,
    trialPlan,
    trialPlans,
    selectTrialPlan,
    refreshPaymentRequest,
    handlePaymentError,
  } = useTrialPayment();

  const {
    trialDays,
    recurringPriceFormatted,
    billingFrequencyLabel,
  } = usePolicyText();

  useEffect(() => {
    const init = async () => {
      if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
        await subscriptionStore.fetchPlans();
      }
      const applePayReady = await initializeApplePay();
      if (!applePayReady) {
        const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
        setError(errorMsg);
      }

      if (!applePayLoadStatusReportedRef.current) {
        applePayLoadStatusReportedRef.current = true;
        if (applePayReady) {
          sendLoadSuccess();
        }
        else {
          sendPaymentFailed('Apple Pay is not available on this device. Please use Safari on iOS or macOS.');
        }
      }
      setIsPageLoading(false);
      syncPaywallMetaFromUrl(searchParams);
    };
    void init();
  }, [initializeApplePay, searchParams]);

  const weeklyTrialPlan = trialPlans.find((p) => p.billingFrequency === 'weekly') ?? trialPlan ?? null;

  useEffect(() => {
    if (!weeklyTrialPlan?.planId) return;
    if (weeklyTrialPlan.planId !== trialPlan?.planId) {
      selectTrialPlan(weeklyTrialPlan.planId);
    }
  }, [weeklyTrialPlan?.planId, trialPlan?.planId, selectTrialPlan]);

  useEffect(() => {
    if (!trialPlan?.planId) return;
    void refreshPaymentRequest();
  }, [trialPlan?.planId, refreshPaymentRequest]);

  const onCta = () => {
    if (processing || isInitializing || !isApplePayReady) return;
    if (!preferredPaymentMethod) {
      const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
      setError(errorMsg);
      sendPaymentFailed(errorMsg);
      return;
    }
    setError('');
    setProcessing(true);
    void showApplePay(paywallFlowStore.meta.trim() || undefined)
      .catch((err: unknown) => {
        if (isApplePayCancelledError(err)) {
          return;
        }

        setError(handlePaymentError(err));
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const frequencyLabel = billingFrequencyLabel === 'month' ? 'месяц' : billingFrequencyLabel === 'week' ? 'неделя' : 'год';

  return (
    <PageOuter>
      <PageColumn>
        <PageBg />
        {SHOW_VIDEO && (
          <VideoBackdrop>
            <Video
              src={VIDEO_SRC}
              autoPlay
              muted
              loop
              playsInline
              onLoadedData={() => setIsPageLoading(false)}
            />
          </VideoBackdrop>
        )}
        <Overlay />
        <PageLayout>
          <Screen>
            <VideoSection />
            <ContentPush>
              <ContentInner>
                <Title>Используйте VPN,</Title>
                <Title>чтобы</Title>
                <Title>разблокировать сайты для взрослых</Title>
                <Offer>
                  {trialDays}
                  -дневная
                  <OrangeAccent>бесплатная пробная</OrangeAccent>
                  {' '}
                  версия
                </Offer>
                <Price>
                  Затем
                  {recurringPriceFormatted}
                  {' '}
                  / 1
                  {frequencyLabel}
                </Price>

                <CtaWrap>
                  <CtaButton
                    type="button"
                    $disabled={processing || isInitializing || !isApplePayReady || isPageLoading}
                    onClick={onCta}
                  >
                    Продолжить
                  </CtaButton>
                </CtaWrap>

                <ErrorWrapper>
                  <PaymentError error={error} variant="dark" />
                </ErrorWrapper>

                <NoCharge>
                  <CheckIcon>✓</CheckIcon>
                  Сегодня оплата не требуется!
                </NoCharge>
              </ContentInner>
            </ContentPush>
          </Screen>
          <FooterWrap>
            <Legal>
              Продолжая, вы оформляете подписку, которая автоматически продлевается в конце выбранного периода, если не отменена как минимум за 24 часа до окончания текущего периода. Чтобы избежать списания средств, пожалуйста, свяжитесь с нашей службой поддержки для отмены как минимум за 24 часа до окончания периода. Если вы не уверены, как отменить подписку, обратитесь к нашим
              {' '}
              <Link to="/policy/terms">Условиям использования</Link>
              . Вы можете сохранить скриншот этой информации для справки. Продолжая, вы также соглашаетесь с нашей
              {' '}
              <Link to="/policy/privacy">Политикой конфиденциальности приложения</Link>
              ,
              {' '}
              <Link to="/policy/privacy">Политикой конфиденциальности веб-сайта</Link>
              {' '}
              и
              {' '}
              <Link to="/policy/terms">Условиями использования</Link>
              .
            </Legal>
            <FooterSection>
              <RestoreSubscription />
            </FooterSection>
          </FooterWrap>
        </PageLayout>
      </PageColumn>
    </PageOuter>
  );
}

export const PaywallV4 = observer(PaywallV4Inner);
